import type { WebPubSubClient } from "@azure/web-pubsub-client";
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  getOverallStatusFromLogs,
  type KintoneSystem,
  KintoneSystemApi,
  type ProcessName,
  type ProcessStatus,
} from "~/functions/api/kintoneSystem";
import type { RootState } from "~/store";
import {
  buildAppGenerationOverallNotification,
  type NotifiedOverallStatus,
  shouldNotifyOverallStatus,
} from "~/utils/appGenerationNotificationUtils";
import { useNativePushNotification } from "~/utils/useNativePushNotification";
import { generateJobStatusGroupName, joinGroup, leaveGroup } from "~/utils/webPubSubUtils";

interface JobStatusMessage {
  job_id: string;
  process_name: ProcessName;
  status: ProcessStatus;
  at: string;
}

export const useAppGenerationNotifications = (client: WebPubSubClient | null) => {
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const { sendNotificationWithNavigation } = useNativePushNotification();

  const subscribedJobsRef = useRef<Map<string, KintoneSystem>>(new Map());
  const lastNotifiedOverallStatusByJobIdRef = useRef<Map<string, NotifiedOverallStatus>>(new Map());
  const kintoneSystemApiRef = useRef(new KintoneSystemApi());
  const isSetupRef = useRef(false);

  const handleJobStatusMessage = useCallback(
    (e: any) => {
      try {
        const message = JSON.parse(e.message.data as string) as JobStatusMessage;

        const jobInfo = subscribedJobsRef.current.get(message.job_id);
        if (!jobInfo) {
          return;
        }

        const nextLogs = {
          ...jobInfo.logs,
          [message.process_name]: { status: message.status },
        };
        const overallStatus = getOverallStatusFromLogs(nextLogs);

        subscribedJobsRef.current.set(message.job_id, { ...jobInfo, logs: nextLogs });

        if (overallStatus !== "succeeded" && overallStatus !== "failed") return;

        const lastNotified =
          lastNotifiedOverallStatusByJobIdRef.current.get(message.job_id) ?? null;
        if (!shouldNotifyOverallStatus(lastNotified, overallStatus)) return;
        if (!currentUser?.organization_id || !currentProject?.id) return;

        lastNotifiedOverallStatusByJobIdRef.current.set(message.job_id, overallStatus);
        sendNotificationWithNavigation(
          buildAppGenerationOverallNotification({
            organizationId: currentUser.organization_id,
            projectId: currentProject.id,
            systemId: jobInfo.id,
            spaceName: jobInfo.space?.name,
            status: overallStatus,
          }),
        );

        // best-effort cleanup: stop listening to terminal job groups
        if (client) {
          void leaveGroup(client, generateJobStatusGroupName(message.job_id));
        }
        subscribedJobsRef.current.delete(message.job_id);
        lastNotifiedOverallStatusByJobIdRef.current.delete(message.job_id);
      } catch (error) {
        console.error("[useAppGenerationNotifications] Failed to parse message:", error);
      }
    },
    [client, currentProject, currentUser, sendNotificationWithNavigation],
  );

  useEffect(() => {
    if (!client || !currentProject?.id || !currentUser?.organization_id) {
      return;
    }
    if (typeof (client as any).on !== "function") {
      return;
    }

    const setup = async () => {
      if (isSetupRef.current) return;
      isSetupRef.current = true;

      try {
        const systems = await kintoneSystemApiRef.current.getKintoneSystemsByProject(
          currentUser.organization_id,
          currentProject.id,
        );

        if (!systems) return;

        const runningJobs = systems.filter((s) => s.status === "queued" || s.status === "running");

        for (const job of runningJobs) {
          if (!subscribedJobsRef.current.has(job.job_id)) {
            const groupName = generateJobStatusGroupName(job.job_id);
            await joinGroup(client, groupName);
            subscribedJobsRef.current.set(job.job_id, job);
          }
        }

        (client as any).on("group-message", handleJobStatusMessage);
      } catch (error) {
        console.error("[useAppGenerationNotifications] Failed to setup:", error);
        isSetupRef.current = false;
      }
    };

    setup();

    return () => {
      const cleanup = async () => {
        if (client) {
          if (typeof (client as any).off === "function") {
            (client as any).off("group-message", handleJobStatusMessage);
          }

          for (const [jobId] of Array.from(subscribedJobsRef.current)) {
            const groupName = generateJobStatusGroupName(jobId);
            await leaveGroup(client, groupName);
          }
          subscribedJobsRef.current.clear();
          lastNotifiedOverallStatusByJobIdRef.current.clear();
        }
        isSetupRef.current = false;
      };
      cleanup();
    };
  }, [client, currentProject?.id, currentUser?.organization_id, handleJobStatusMessage]);

  const subscribeToJob = useCallback(
    async (job: KintoneSystem) => {
      if (!client || subscribedJobsRef.current.has(job.job_id)) {
        return;
      }

      const groupName = generateJobStatusGroupName(job.job_id);
      await joinGroup(client, groupName);
      subscribedJobsRef.current.set(job.job_id, job);
    },
    [client],
  );

  const unsubscribeFromJob = useCallback(
    async (jobId: string) => {
      if (!client || !subscribedJobsRef.current.has(jobId)) {
        return;
      }

      const groupName = generateJobStatusGroupName(jobId);
      await leaveGroup(client, groupName);
      subscribedJobsRef.current.delete(jobId);
      lastNotifiedOverallStatusByJobIdRef.current.delete(jobId);
    },
    [client],
  );

  return {
    subscribeToJob,
    unsubscribeFromJob,
  };
};
